import React from "react";
import { Link, useLocation } from "react-router-dom";
import { SubscriptionUser } from "../types/subscription-user.entity";
import { FaExternalLinkAlt } from "react-icons/fa";
import { useTheme } from "src/context/theme.context";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";

type Props = { data: (SubscriptionUser & { id: string })[] };

const statusClass = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "CANCELLED":
      return "bg-red-100 text-red-700 border-red-200";
    case "EXPIRED":
      return "bg-gray-100 text-gray-700 border-gray-200";
    default:
      return "bg-amber-100 text-amber-700 border-amber-200";
  }
};

const providerClass = (provider: string) => {
  switch (provider) {
    case "TAALY":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "APPLE":
      return "bg-gray-100 text-gray-700 border-gray-200";
    case "GOOGLE":
      return "bg-red-100 text-red-700 border-red-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const formatDate = (ts: any) => new Date(ts?.toDate?.() || ts).toLocaleDateString();
const formatTime = (ts: any) => new Date(ts?.toDate?.() || ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export const SubscriptionUsersTable: React.FC<Props> = ({ data }) => {
  const { isDarkMode } = useTheme();
  const { search } = useLocation();

  return (
    <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
      <table
        className={`min-w-[1200px] sm:min-w-full divide-y text-[11px] sm:text-xs ${
          isDarkMode ? "border border-slate-700 divide-slate-700" : "border border-gray-200 divide-gray-200"
        }`}
      >
        <thead className={`text-center text-[10px] sm:text-xs ${isDarkMode ? "bg-slate-800 text-slate-100" : "bg-gray-900 text-white"}`}>
          <tr>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">User</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Status</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Provider</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Plan</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Started</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={6} className={`py-4 text-center text-xl ${isDarkMode ? "text-slate-300" : "text-gray-500"}`}>No Subscription Users</td>
            </tr>
          )}
          {data.map((user) => (
            <tr
              key={user.id}
              className={`text-center ${
                isDarkMode
                  ? "odd:bg-slate-900 even:bg-slate-800/70 hover:bg-slate-800"
                  : "odd:bg-white even:bg-gray-50 hover:bg-gray-100"
              }`}
            >
              <td className={`px-2 py-2 sm:px-4 sm:py-3 border-t ${isDarkMode ? "border-slate-700" : "border-gray-200"}`}>
                <Link
                  to={`/users/${user.id}`}
                  className={`flex items-center gap-2 p-2 rounded-lg border transition-all duration-200 cursor-pointer group no-underline ${
                    isDarkMode
                      ? "bg-gradient-to-r from-sky-500/10 to-indigo-500/10 border-sky-500/20 hover:shadow-md hover:from-sky-500/20 hover:to-indigo-500/20 hover:border-sky-400/30"
                      : "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 hover:shadow-md hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300"
                  }`}
                  title={`View User: ${user.id}`}
                >
                  <div className="flex items-center justify-center px-2 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded text-white text-[8px] sm:text-[9px] font-bold uppercase tracking-wide group-hover:from-blue-600 group-hover:to-indigo-600 transition-all duration-200">
                    User
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-[9px] sm:text-[10px] font-mono font-bold transition-colors duration-200 break-all ${
                        isDarkMode ? "text-sky-200 group-hover:text-sky-100" : "text-blue-700 group-hover:text-blue-900"
                      }`}
                    >
                      {user.id}
                    </div>
                  </div>
                </Link>
              </td>
              <td className={`px-2 py-2 sm:px-4 sm:py-3 border-t ${isDarkMode ? "border-slate-700" : "border-gray-200"}`}>
                {user.activeSubscription ? (
                  <span className={`px-2 py-1 rounded-full border text-xs ${statusClass(user.activeSubscription.status)}`}>
                    {user.activeSubscription.status}
                  </span>
                ) : (
                  <span className={`text-[10px] sm:text-[11px] ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>N/A</span>
                )}
              </td>
              <td className={`px-2 py-2 sm:px-4 sm:py-3 border-t ${isDarkMode ? "border-slate-700" : "border-gray-200"}`}>
                {user.activeSubscription ? (
                  <span className={`px-2 py-1 rounded-full border text-xs ${providerClass(user.activeSubscription.provider)}`}>
                    {user.activeSubscription.provider}
                  </span>
                ) : (
                  <span className={`text-[10px] sm:text-[11px] ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>N/A</span>
                )}
              </td>
              <td className={`px-2 py-2 sm:px-4 sm:py-3 border-t ${isDarkMode ? "border-slate-700" : "border-gray-200"}`}>
                {user.activeSubscription ? (
                  <div className="flex items-center justify-center">
                    <span className={`text-[10px] sm:text-[11px] font-semibold ${isDarkMode ? "text-slate-100" : "text-gray-700"}`}>
                      {user.activeSubscription.plan}
                    </span>
                  </div>
                ) : (
                  <span className={`text-[10px] sm:text-[11px] ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>N/A</span>
                )}
              </td>
              <td className={`px-2 py-2 sm:px-4 sm:py-3 border-t ${isDarkMode ? "border-slate-700" : "border-gray-200"}`}>
                {user.activeSubscription ? (
                  <div className="flex flex-col leading-tight">
                    <span className={`text-[10px] sm:text-[11px] font-semibold ${isDarkMode ? "text-slate-100" : "text-gray-700"}`}>
                      {formatDate(user.activeSubscription.startDate)}
                    </span>
                    <span className={`text-[9px] sm:text-[10px] ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>
                      {formatTime(user.activeSubscription.startDate)}
                    </span>
                  </div>
                ) : (
                  <span className={`text-[10px] sm:text-[11px] ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>N/A</span>
                )}
              </td>
              <td className={`px-2 py-2 sm:px-4 sm:py-3 border-t ${isDarkMode ? "border-slate-700" : "border-gray-200"}`}>
                <Link
                  to={appendSearchToPath(`/subscription-users/${user.id}`, search)}
                  className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2"
                >
                  <FaExternalLinkAlt />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SubscriptionUsersTable;
